import React, { FC, useEffect, useState, useRef } from 'react';
import { Stack, Paper, Divider, useTheme } from '@mui/material';
import { useSnapshot } from 'valtio';
import { equalLengthAndValues, toHumanReadable, useKeys } from '@core/logic';
import {
  Depth,
  Width,
  attributeTypes,
  biggerDepths,
  biggerWidths,
  defaultDepths,
  defaultWidths,
  layingTypes,
  roadContexts,
  smallerDepths,
  surfaceTypes,
} from '@trenches/types';
import { DepthColors, PointColors, roadContextColors, WidthColors } from '@trenches/colors';
import { LegendCollapseBtn } from './LegendCollapseBtn';
import { LegendItems } from './LegendItems';
import { LegendNav } from './LegendNav';
import {
  attributeState,
  setDepth,
  setLayingType,
  setRoadContext,
  setSelectedAttributeType,
  setSurfaceType,
  setWidth,
  toggleShowOnlyCurrentDepth,
  toggleShowOnlyCurrentWidth,
} from './state';
import type { LegendItemsDataProps } from './types';

export const AttributeLegend: FC = () => {
  const theme = useTheme();
  const { layingType, surfaceType, selectedAttributeType, depth, width, roadContext } =
    useSnapshot(attributeState);
  const [soilDisplacementKeyPressed, removeKeyPressed, attributeSwitchKey] = useKeys('s', 'r', '3');

  const [visibleDepths, setVisibleDepths] = useState<Depth[]>([...defaultDepths]);
  const [visibleWidths, setVisibleWidths] = useState<Width[]>([...defaultWidths]);

  const containerRef = useRef<HTMLDivElement>(null);

  const areSmallerDepthsVisible = visibleDepths.includes(smallerDepths[0]);
  const areBiggerDepthsVisible = visibleDepths.includes(biggerDepths[0]);
  const areDefaultWidthsVisible = equalLengthAndValues(visibleWidths, [...defaultWidths]);

  const setToSoilDisplacement = (): void => setLayingType('SOIL_DISPLACEMENT');

  const legendItemsProps: LegendItemsDataProps = {
    SURFACE_TYPE: {
      attributeList: surfaceTypes,
      attribute: surfaceType,
      callback: setSurfaceType as unknown as (surfaceType: string | null) => void,
      colors: PointColors,
      selected: surfaceType === null,
      onRemove: () => setSurfaceType(null),
    },
    LAYING_TYPE: {
      //NOT_APPLICABLE should be hidden https://deepup-gmbh.atlassian.net/browse/DP-10267
      attributeList: layingTypes.filter((type) => type !== 'NOT_APPLICABLE'),
      attribute: layingType,
      callback: setLayingType as unknown as (layingType: string | null) => void,
      colors: PointColors,
      selected: layingType === null,
      onRemove: () => setLayingType(null),
    },
    DEPTH: {
      attributeList: visibleDepths,
      attribute: depth,
      callback: setDepth as unknown as (depth: string | null) => void,
      colors: DepthColors,
      selected: depth === null,
      onRemove: () => setDepth(null),
    },
    WIDTH: {
      attributeList: visibleWidths,
      attribute: width,
      callback: setWidth as unknown as (width: string | null) => void,
      colors: WidthColors,
      selected: width === null,
      onRemove: () => setWidth(null),
    },
    ROAD_CONTEXT: {
      attributeList: roadContexts,
      attribute: roadContext,
      callback: setRoadContext as unknown as (roadContext: string | null) => void,
      colors: roadContextColors,
      selected: roadContext === null,
      onRemove: () => setRoadContext(null),
    },
  };

  const setToRemoveAttribute = legendItemsProps[selectedAttributeType].onRemove;

  const nextAttributeType = () => {
    const currentAttributeIndex = attributeTypes.indexOf(selectedAttributeType);
    if (currentAttributeIndex === attributeTypes.length - 1) {
      setSelectedAttributeType(attributeTypes[0]);
    } else {
      setSelectedAttributeType(attributeTypes[currentAttributeIndex + 1]);
    }
  };

  const previousAttributeType = () => {
    const currentAttributeIndex = attributeTypes.indexOf(selectedAttributeType);
    if (currentAttributeIndex === 0) {
      setSelectedAttributeType(attributeTypes[attributeTypes.length - 1]);
    } else {
      setSelectedAttributeType(attributeTypes[currentAttributeIndex - 1]);
    }
  };

  const toggleDepthItems = (isSmaller: boolean) => {
    if (isSmaller) {
      setVisibleDepths([
        ...(areSmallerDepthsVisible ? [] : smallerDepths),
        ...defaultDepths,
        ...(areBiggerDepthsVisible ? biggerDepths : []),
      ]);
    } else {
      setVisibleDepths([
        ...(areSmallerDepthsVisible ? smallerDepths : []),
        ...defaultDepths,
        ...(areBiggerDepthsVisible ? [] : biggerDepths),
      ]);
    }
  };

  const toggleWidthItems = () =>
    setVisibleWidths([...defaultWidths, ...(areDefaultWidthsVisible ? biggerWidths : [])]);

  const toggleVisibility = () => {
    if (selectedAttributeType === 'DEPTH') {
      toggleShowOnlyCurrentDepth();
    }
    if (selectedAttributeType === 'WIDTH') {
      toggleShowOnlyCurrentWidth();
    }
  };

  useEffect(() => {
    if (!soilDisplacementKeyPressed) return;
    setToSoilDisplacement();
  }, [soilDisplacementKeyPressed]);

  useEffect(() => {
    if (attributeSwitchKey) {
      nextAttributeType();
    }
  }, [attributeSwitchKey]);

  useEffect(() => {
    if (!removeKeyPressed) return;
    setToRemoveAttribute();
  }, [removeKeyPressed]);

  return (
    <Stack
      ref={containerRef}
      gap={1}
      width={250}
      position="absolute"
      bottom={theme.spacing(3)}
      right={theme.spacing(3)}
      sx={{
        maxHeight: `calc(80vh - ${theme.spacing(6)})`,
        overflow: 'hidden',
        '& > .main-paper': {
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        },
      }}
    >
      <Paper elevation={2} className="main-paper">
        {selectedAttributeType === 'DEPTH' && (
          <>
            <LegendCollapseBtn
              onClick={() => toggleDepthItems(true)}
              label="< 45cm"
              collapsed={areSmallerDepthsVisible}
            />
            <Divider />
          </>
        )}

        <LegendItems
          toggleVisibility={toggleVisibility}
          currentType={selectedAttributeType}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...legendItemsProps[selectedAttributeType]}
        />

        {selectedAttributeType === 'DEPTH' && (
          <>
            <Divider />
            <LegendCollapseBtn
              onClick={() => toggleDepthItems(false)}
              label="> 100cm"
              collapsed={!areBiggerDepthsVisible}
            />
          </>
        )}

        {selectedAttributeType === 'WIDTH' && (
          <>
            <Divider />
            <LegendCollapseBtn
              onClick={toggleWidthItems}
              label="> 45cm"
              collapsed={areDefaultWidthsVisible}
            />
          </>
        )}
      </Paper>
      <LegendNav
        label={toHumanReadable(selectedAttributeType)}
        onPrevClick={previousAttributeType}
        onNextClick={nextAttributeType}
      />
    </Stack>
  );
};
